import { useTheme } from "styled-components";
import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { TinyColor } from "@ctrl/tinycolor";
import { Box } from "../Box";
import { CardBody } from "../Card";
import { Flex } from "../Flex";
import { Tooltip } from "../Tooltip";
import { EmptyChart } from "./EmptyChart";
import { getDataFillColor } from "./helpers";
/**
 * Get the background color for the circle.
 * If there is only one data point, we blend the background color with the data point color.
 * If there are multiple data points, we mix with the progress circle background color, ususaly a card.
 */
function getBackgroundColor(data, theme) {
    if (data.length === 1) {
        const base = new TinyColor(data[0].fill);
        const bg = new TinyColor(theme.colors.cardColors.cardBackground);
        // Blend 90% background, 10% main color
        return bg.isDark() ? base.mix(bg, 80).toRgbString() : base.mix(bg, 90).toRgbString();
    }
    return theme.colors.chartColors.progressCircleBackground;
}
/**
 * The `ProgressCircle` component is built using the `Pie` and `PieChart` component from the [Recharts Pie API](https://recharts.org/en-US/api/Pie).
 */
export const ProgressCircle = ({ cursor, data, isAnimationActive = false, emptyMessage, emptyMessageFontSize = "13px", responsiveContainerHeight = "100%", responsiveContainerWidth = "100%", text, title, titleTooltipText, tooltip, variant = "small", }) => {
    const theme = useTheme();
    const { fontSizes } = theme;
    const pieChartHasData = useMemo(() => (data === null || data === void 0 ? void 0 : data.length) > 0, [data]);
    // If only one data point is provded we color the background of the circle with the color of the data point
    const backgroundColor = getBackgroundColor(data, theme);
    // We calculate the remaining data points and add them to the data array
    const dataCalculated = useMemo(() => {
        const dataCopy = [...data];
        const total = dataCopy.reduce((acc, entry) => acc + entry.value, 0);
        if (total < 100) {
            // The rest of the circle is transparent
            dataCopy.push({
                fill: "transparent",
                name: "Rest",
                value: 100 - total,
            });
        }
        return dataCopy;
    }, [data]);
    const needsResponsiveContainer = typeof responsiveContainerWidth === "string" || typeof responsiveContainerHeight === "string";
    return pieChartHasData ? (React.createElement(CardBody, { sx: {
            cursor,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
        }, "data-testid": "rechartsProgressCircleCardBody" },
        React.createElement(Flex, { "data-testid": "rechartsProgressRelativePositioningContainer", sx: {
                cursor,
                flexDirection: "column",
                position: "relative",
            } },
            React.createElement(Flex, { sx: {
                    alignItems: "center",
                    cursor,
                    fontSize: variant === "large" ? fontSizes[5] : fontSizes[2],
                    height: "100%",
                    justifyContent: "center",
                    position: "absolute",
                    width: "100%",
                    zIndex: 1, // Otherwise cursor does not show
                }, "data-testid": "rechartsProgressCircleTextContainer" }, text),
            React.createElement(ResponsiveContainer, { id: "rechartsProgressCircleDiv", height: responsiveContainerHeight, width: needsResponsiveContainer ? responsiveContainerWidth : "100%" },
                React.createElement(PieChart, Object.assign({}, (needsResponsiveContainer
                    ? {}
                    : { height: responsiveContainerHeight, width: responsiveContainerWidth })),
                    React.createElement(Pie, { cornerRadius: 12, stroke: "none", data: [{ fill: backgroundColor, name: "Rest", value: 100 }], dataKey: "value", endAngle: -60, innerRadius: variant === "large" ? 55 : 30, outerRadius: variant === "large" ? 65 : 35, isAnimationActive: false, cx: "50%", cy: "50%", startAngle: 240 }),
                    React.createElement(Pie, { cornerRadius: 12, cursor: cursor, cx: "50%", cy: "50%", data: dataCalculated, dataKey: "value", endAngle: -60, innerRadius: variant === "large" ? 55 : 30, isAnimationActive: isAnimationActive, nameKey: "name", outerRadius: variant === "large" ? 65 : 35, startAngle: 240, stroke: "none" }, data.map((entry, index) => (React.createElement(Cell, { key: `cell-${entry.name}`, fill: entry.fill ||
                            getDataFillColor({
                                entry,
                                index,
                                isRiskChart: false,
                                theme,
                            }) })))),
                    tooltip))),
        title && (React.createElement(Tooltip, { content: titleTooltipText },
            React.createElement(Box, { style: {
                    cursor: "inherit",
                    paddingTop: "8px",
                    textAlign: "center",
                }, "data-testid": "rechartsProgressCircleTitle" }, title))))) : (React.createElement(Box, { "data-testid": "rechartsProgressCircleNoData", style: { height: responsiveContainerHeight, width: responsiveContainerWidth } },
        React.createElement(EmptyChart, { emptyMessage: emptyMessage, emptyMessageFontSize: emptyMessageFontSize })));
};
